import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import type { ReglagesAssurance } from '@artosera/shared';
import {
  messageErreurAssurance,
  useReglagesAssurance,
  useModifierReglagesAssurance,
} from '../../api/assurance.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import styles from '../assurance/AssurancePage.module.css';

function Formulaire({
  galerieSlug,
  reglages,
  peutEcrire,
  recharger,
}: {
  galerieSlug: string;
  reglages: ReglagesAssurance;
  peutEcrire: boolean;
  recharger: () => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const modifier = useModifierReglagesAssurance(galerieSlug);
  const [periodicite, setPeriodicite] = useState(String(reglages.periodiciteMois));
  const [seuil, setSeuil] = useState(String(reglages.seuilComparablePct));
  const [erreur, setErreur] = useState('');
  const [conflit, setConflit] = useState(false);
  const [succes, setSucces] = useState(false);
  async function soumettre() {
    setErreur('');
    setSucces(false);
    try {
      await modifier.mutateAsync({
        periodiciteMois: Number(periodicite),
        seuilComparablePct: Number(seuil),
        revision: reglages.revision,
      });
      setSucces(true);
    } catch (e) {
      setErreur(messageErreurAssurance(e));
      setConflit(estApiError(e) && e.status === 409);
    }
  }
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        if (peutEcrire && !conflit) void soumettre();
      }}
    >
      <Field htmlFor="assurance-periodicite" label={t('regie:assurance.reglages.periodicite')}>
        <Input
          id="assurance-periodicite"
          type="number"
          min={1}
          max={120}
          required
          disabled={!peutEcrire || conflit}
          value={periodicite}
          onChange={(e) => setPeriodicite(e.target.value)}
        />
      </Field>
      <Field htmlFor="assurance-seuil" label={t('regie:assurance.reglages.seuil')}>
        <Input
          id="assurance-seuil"
          type="number"
          min={1}
          max={500}
          required
          disabled={!peutEcrire || conflit}
          value={seuil}
          onChange={(e) => setSeuil(e.target.value)}
        />
      </Field>
      {erreur && <p role="alert">{erreur}</p>}
      {succes && <p role="status">{t('regie:assurance.reglages.succes')}</p>}
      {conflit && (
        <Button type="button" onClick={recharger}>
          {t('regie:assurance.recharger')}
        </Button>
      )}
      {peutEcrire && (
        <Button type="submit" loading={modifier.isPending} disabled={conflit}>
          {t('commun:actions.enregistrer')}
        </Button>
      )}
    </form>
  );
}
export function AssuranceReglagesPage() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const reglages = useReglagesAssurance(galerieSlug);
  const [version, setVersion] = useState(0);
  return (
    <div className={styles.page}>
      <h1 className={styles.titre}>{t('regie:reglages.assurance.titre')}</h1>
      <p>{t('regie:reglages.assurance.sousTitre')}</p>
      {reglages.isPending && <p role="status">{t('commun:chargement')}</p>}
      {reglages.isError && <p role="alert">{messageErreurAssurance(reglages.error)}</p>}
      {reglages.data && (
        <Formulaire
          key={version}
          galerieSlug={galerieSlug}
          reglages={reglages.data}
          peutEcrire={galerieALaction(galerie, 'insurance:settings')}
          recharger={() => {
            void reglages.refetch().then((r) => {
              if (r.isSuccess) setVersion((v) => v + 1);
            });
          }}
        />
      )}
    </div>
  );
}
